import React from 'react';
import styled from 'styled-components';

const SupportContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const SupportHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SupportTitle = styled.h1`
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00ff88, #00d4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SupportSubtitle = styled.p`
  font-size: 1.2rem;
  color: #cccccc;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const SupportCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 255, 136, 0.3);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-5px);
    border-color: #00ff88;
    box-shadow: 0 10px 30px rgba(0, 255, 136, 0.2);
  }
`;

const CardTitle = styled.h3`
  color: #00ff88;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CardContent = styled.div`
  color: #cccccc;
  line-height: 1.6;
`;

const ContactInfo = styled.div`
  background: rgba(0, 255, 136, 0.1);
  border: 1px solid #00ff88;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  margin-top: 2rem;
`;

const ContactTitle = styled.h2`
  color: #00ff88;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const ContactItem = styled.div`
  margin: 1rem 0;
  font-size: 1.1rem;
  color: #ffffff;
`;

const ContactLink = styled.a`
  color: #00ff88;
  text-decoration: none;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;

const FAQSection = styled.div`
  margin-top: 3rem;
`;

const FAQTitle = styled.h2`
  color: #ffffff;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const FAQItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const FAQQuestion = styled.h4`
  color: #00ff88;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const FAQAnswer = styled.p`
  color: #cccccc;
  line-height: 1.6;
`;

const Support = () => {
  return (
    <SupportContainer>
      <SupportHeader>
        <SupportTitle>Customer Support</SupportTitle>
        <SupportSubtitle>
          We're here to help with any questions or issues you may have. 
          Our dedicated support team is available to assist you.
        </SupportSubtitle>
      </SupportHeader>

      <SupportGrid>
        <SupportCard>
          <CardTitle>📞 Phone Support</CardTitle>
          <CardContent>
            <p>Call our customer service line for immediate assistance:</p>
            <ContactItem>
              <strong>Customer Service:</strong> (617) 370-1566
            </ContactItem>
            <p>Available for general inquiries, technical support, and billing questions.</p>
          </CardContent>
        </SupportCard>

        <SupportCard>
          <CardTitle>📧 Email Support</CardTitle>
          <CardContent>
            <p>Send us an email for detailed assistance:</p>
            <ContactItem>
              <ContactLink href="mailto:connectinoutinc@gmail.com">
                connectinoutinc@gmail.com
              </ContactLink>
            </ContactItem>
            <p>We typically respond within 24 hours for all email inquiries.</p>
          </CardContent>
        </SupportCard>

        <SupportCard>
          <CardTitle>🔧 Technical Support</CardTitle>
          <CardContent>
            <p>Having technical issues? We can help with:</p>
            <ul>
              <li>Account setup and management</li>
              <li>Payment and billing issues</li>
              <li>Phone number assignments</li>
              <li>Message delivery problems</li>
              <li>System access issues</li>
            </ul>
          </CardContent>
        </SupportCard>

        <SupportCard>
          <CardTitle>💰 Billing Support</CardTitle>
          <CardContent>
            <p>Questions about payments or account balances?</p>
            <ul>
              <li>Payment processing</li>
              <li>Account balance inquiries</li>
              <li>Refund requests</li>
              <li>Payment method updates</li>
              <li>Billing statement questions</li>
            </ul>
          </CardContent>
        </SupportCard>
      </SupportGrid>

      <ContactInfo>
        <ContactTitle>Get in Touch</ContactTitle>
        <ContactItem>
          <strong>Customer Service Phone:</strong> (617) 370-1566
        </ContactItem>
        <ContactItem>
          <strong>Email:</strong>{' '}
          <ContactLink href="mailto:connectinoutinc@gmail.com">
            connectinoutinc@gmail.com
          </ContactLink>
        </ContactItem>
        <ContactItem>
          <strong>Address:</strong> PO Box 35437, Boston, MA 02135
        </ContactItem>
      </ContactInfo>

      <FAQSection>
        <FAQTitle>Frequently Asked Questions</FAQTitle>
        
        <FAQItem>
          <FAQQuestion>How do I create an account?</FAQQuestion>
          <FAQAnswer>
            Visit our sign-up page and follow the simple registration process. 
            You'll need to provide basic information and verify your identity.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>How do I load money into an inmate's account?</FAQQuestion>
          <FAQAnswer>
            After creating an account, you can load funds through our secure payment system. 
            We accept major credit cards and bank transfers.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>How do I get a phone number for an inmate?</FAQQuestion>
          <FAQAnswer>
            Phone numbers are automatically assigned when you create an inmate account. 
            Each inmate receives a unique Skylink number for secure communication.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>What if I have billing questions?</FAQQuestion>
          <FAQAnswer>
            Contact our billing support team via email or call our customer service line. 
            We're happy to help with any payment-related inquiries.
          </FAQAnswer>
        </FAQItem>

        <FAQItem>
          <FAQQuestion>How secure is the communication system?</FAQQuestion>
          <FAQAnswer>
            We use advanced encryption and security protocols to ensure all communications 
            remain private and protected. Our system is designed specifically for correctional facilities.
          </FAQAnswer>
        </FAQItem>
      </FAQSection>
    </SupportContainer>
  );
};

export default Support; 